import React, { useEffect, useRef, useState } from 'react';
import ReactCrop from 'react-image-crop';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { FullWidthEditorProps } from './FocusPointEditor.types';
import { EditorLayout } from './EditorLayout';
import { Box, useToast } from '@withjoy/joykit';
import { WarningTriangle } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core';
import { addError } from '@shared/utils/logger';
import { EventPageType } from '@graphql/generated';
import { TempSpinner } from '../TempSpinner';
import { SkeletonThumbnail } from '../Skeleton';
import { useFocusPointEditorTelemetry } from './FocusPointEditor.telemetry';
import { CROP_ASPECT_RATIO } from './FocusPointEditor.constant';
import { useFullWidthEditor } from './useFullWidthEditor';

import 'react-image-crop/dist/ReactCrop.css';
import './FullWidthEditor.css';

export const FullWidthPhotoEditor = (props: FullWidthEditorProps) => {
  const {
    crop,
    setCrop,
    isLoadingPhoto,
    imageData,
    handleCloseClick,
    isUpdatingEventPhoto,
    isDeletingPagePhoto,
    handleRemovePhoto,
    handleChangePhoto,
    handleCancelClick,
    onCropComplete,
    pageId,
    pageSlug,
    pageType,
    eventId,
    telemetryCategory,
    handleSaveUploadedPhoto,
    uploadedImageData,
    uploadedImagePhotoId,
    updatePagePhotoPreview,
    photoPresentationLayout,
    refetchQueryList = [],
    handleOnCancel,
  } = props;

  const [isLoadingLatestImage, setIsLoadingLatestImage] = useState(true);

  const previousImageURL = useRef(imageData.url);

  const { saveFullWidthPhotoChangesButtonClicked } = useFocusPointEditorTelemetry();

  const { toast } = useToast();
  const { tq } = useTranslation('joykit');

  const { handleOnCustomPagePhotoSave, isSavingPageContainerPhotoPresentation, waitingUploadPhoto, handleOnPagePhotoSave, isSavingPagePhotoPresentation } = useFullWidthEditor({
    eventId,
    updatePagePhotoPreview,
    handleOnCancel,
    handleCancelClick,
    telemetryCategory,
    pageId,
    pageSlug,
    pageType,
    refetchQueryList,
    imageData,
  });

  const handleOnSaveClick = useEventCallback(async () => {
    // Saving page photo presentation directly within this editor instead of `EditorConsole`
    // because this component is only ever enabled within the website designer (as oppposed to the travel map).

    if (!pageId) {
      // `pageId` should be provided or else this editor is being used in an unknown context.
      // Track in new relic.
      addError('WebsitePagePhotoEditor - FullWidthEditor - missing pageId', { eventId, photoId: imageData.id, telemetryCategory, pageType });
      return;
    }

    saveFullWidthPhotoChangesButtonClicked(telemetryCategory, { pageId, pageType, cropData: crop });
    try {
      await handleSaveUploadedPhoto();

      if (pageType === EventPageType.custom) {
        handleOnCustomPagePhotoSave({ crop, photoPresentationLayout, uploadedImageData, uploadedImagePhotoId });
      } else {
        handleOnPagePhotoSave({ crop, photoPresentationLayout, uploadedImageData });
      }
    } catch (error) {
      toast(tq`Failed to update photo settings.`, { icon: <WarningTriangle /> });
    }
  });

  // Set loading to true when the image URL changes
  useEffect(() => {
    if (previousImageURL.current !== imageData.url) {
      setIsLoadingLatestImage(true);
      previousImageURL.current = imageData.url;
    }
  }, [imageData.url]);

  const aspectRatio = imageData.width / imageData.height;

  return (
    <EditorLayout
      note={tq`Pick the part of the image that you'd like to show on your site.`}
      isDeletePhotoButtonDisabled={isLoadingPhoto || isDeletingPagePhoto}
      isDeletePhotoButtonLoading={isDeletingPagePhoto}
      onDeletePhotoButtonClick={handleRemovePhoto}
      onReplacePhotoButtonClick={handleChangePhoto}
      isReplacePhotoButtonDisabled={isLoadingPhoto || isSavingPagePhotoPresentation || isSavingPageContainerPhotoPresentation}
      onSavePhotoButtonClick={handleOnSaveClick}
      onCancelButtonClick={handleCancelClick}
      isCancelButtonDisabled={isUpdatingEventPhoto || isSavingPagePhotoPresentation || isSavingPageContainerPhotoPresentation || waitingUploadPhoto}
      onCloseButtonClick={handleCloseClick}
      isCloseButtonDisabled={isUpdatingEventPhoto || isSavingPagePhotoPresentation || isSavingPageContainerPhotoPresentation || waitingUploadPhoto}
      isSavePhotoButtonDisabled={isSavingPagePhotoPresentation || isUpdatingEventPhoto || isLoadingPhoto || isSavingPageContainerPhotoPresentation || waitingUploadPhoto}
      isSavePhotoButtonLoading={isUpdatingEventPhoto || isSavingPagePhotoPresentation || isSavingPageContainerPhotoPresentation || waitingUploadPhoto}
      content={() => {
        return (
          <>
            <ReactCrop
              key={imageData.url}
              crop={crop}
              aspect={CROP_ASPECT_RATIO[photoPresentationLayout]}
              minHeight={100}
              minWidth={100}
              keepSelection
              style={{
                display: isLoadingLatestImage ? 'none' : 'inline-block',
              }}
              disabled={isLoadingPhoto}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              onComplete={(_, pCrop) => {
                onCropComplete?.();
              }}
            >
              <Box
                as="img"
                src={imageData.url}
                onLoad={() => setIsLoadingLatestImage(false)}
              />
              {isLoadingPhoto && (
                <TempSpinner
                  position={'absolute'}
                  className="loading"
                />
              )}
            </ReactCrop>

            {/* for showing image skeleton */}
            <Box
              display={isLoadingLatestImage ? 'block' : 'none'}
              position="relative"
              width="100%"
              maxWidth={imageData.width}
              margin="0 auto"
            >
              <Box
                top="0"
                left="0"
                width="100%"
                height="0"
                paddingBottom={`${(1 / aspectRatio) * 100}%`}
              />
              <SkeletonThumbnail
                position="absolute"
                inset={0}
                width="100%"
                height="100%"
              />
            </Box>
          </>
        );
      }}
    />
  );
};
